import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Users, Star, CreditCard, Zap, Award, Calendar, GraduationCap,
  CheckCheck, Search, ChevronRight, ExternalLink, Loader2, Trophy, Shield,
  Settings, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useApp } from '@/context/AppContext';
import { useNotifications } from '@/context/NotificationContext';
import { cn } from '@/lib/utils';

const TYPE_CFG = {
  application:   { icon: Users,        color: 'text-chart-1', bg: 'bg-chart-1/10',   label: 'Application'    },
  milestone:     { icon: Star,         color: 'text-chart-4', bg: 'bg-chart-4/10',   label: 'Milestone'      },
  payment:       { icon: CreditCard,   color: 'text-chart-3', bg: 'bg-chart-3/10',   label: 'Payment'        },
  match:         { icon: Zap,          color: 'text-primary', bg: 'bg-primary/10',   label: 'AI Match'       },
  certificate:   { icon: Award,        color: 'text-chart-5', bg: 'bg-chart-5/10',   label: 'Certificate'    },
  event:         { icon: Calendar,     color: 'text-chart-2', bg: 'bg-chart-2/10',   label: 'Event'          },
  mentor:        { icon: GraduationCap,color: 'text-warning', bg: 'bg-warning/10',   label: 'Mentor'         },
  gamification:  { icon: Trophy,       color: 'text-warning', bg: 'bg-warning/10',   label: 'Gamification'   },
  change_request:{ icon: Shield,       color: 'text-chart-1', bg: 'bg-chart-1/10',   label: 'Change Request' },
  system:        { icon: Bell,         color: 'text-muted-foreground', bg: 'bg-muted', label: 'System'       },
};

// Relative time formatter
function relTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)   return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const NotificationItem = ({ notif, onRead, onJump }) => {
  const config = TYPE_CFG[notif.type] || TYPE_CFG.system;
  const id     = notif._id || notif.id;

  return (
    <div
      className={cn(
        'flex items-start gap-4 p-4 rounded-xl border transition-colors cursor-pointer group',
        notif.read
          ? 'border-border bg-transparent hover:bg-muted/50'
          : 'border-primary/15 bg-primary/5 hover:bg-primary/8'
      )}
      onClick={() => { onRead(id); if (notif.link) onJump(notif.link); }}
    >
      <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0', config.bg)}>
        <config.icon className={cn('w-4 h-4', config.color)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-medium text-sm text-foreground">{notif.title}</span>
          {!notif.read && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
          <span className={cn(
            'text-[10px] px-1.5 py-0.5 rounded font-medium ml-auto',
            config.bg, config.color
          )}>{config.label}</span>
        </div>
        <p className="text-sm text-muted-foreground">{notif.message}</p>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-muted-foreground">{relTime(notif.createdAt || notif.time)}</span>
          {notif.link && (
            <button
              onClick={(e) => { e.stopPropagation(); onRead(id); onJump(notif.link); }}
              className="text-xs text-primary hover:underline flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ExternalLink className="w-3 h-3" />
              {notif.linkLabel || 'Open'}
            </button>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
};

// ── Preferences panel ─────────────────────────────────────────────────────────
const PrefsPanel = ({ preferences, onSave }) => {
  const [prefs, setPrefs] = useState(preferences);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(prefs);
    setSaving(false);
  };

  return (
    <div className="space-y-4 max-w-sm">
      <h3 className="font-semibold text-foreground text-sm">Notification Preferences</h3>
      {[
        { key: 'emailNotifications', label: 'Email notifications', desc: 'Receive important updates by email' },
        { key: 'pushNotifications',  label: 'Push notifications',  desc: 'Browser/device push alerts' },
      ].map(item => (
        <div key={item.key} className="flex items-start gap-3 p-3 rounded-xl border border-border bg-muted/30">
          <input
            type="checkbox"
            id={item.key}
            checked={!!prefs[item.key]}
            onChange={e => setPrefs(p => ({ ...p, [item.key]: e.target.checked }))}
            className="mt-0.5 accent-primary"
          />
          <label htmlFor={item.key} className="cursor-pointer">
            <p className="text-sm font-medium text-foreground">{item.label}</p>
            <p className="text-xs text-muted-foreground">{item.desc}</p>
          </label>
        </div>
      ))}
      <Button size="sm" onClick={save} disabled={saving} className="gradient-primary text-primary-foreground border-0">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
        Save Preferences
      </Button>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Notifications() {
  const navigate = useNavigate();
  const { unreadCount } = useApp();
  const { notifications, loading, total, preferences, fetchNotifications, markRead, markAllRead, savePreferences } = useNotifications();
  const [filter,   setFilter]   = useState('all');
  const [search,   setSearch]   = useState('');
  const [showPrefs, setShowPrefs] = useState(false);

  // Reload when filter tab changes
  useEffect(() => {
    const params = {};
    if (filter === 'unread') params.unread = true;
    else if (filter !== 'all') params.type = filter;
    fetchNotifications(params);
  }, [filter]);

  const filtered = notifications.filter(n => {
    if (!search) return true;
    return (
      n.message?.toLowerCase().includes(search.toLowerCase()) ||
      n.title?.toLowerCase().includes(search.toLowerCase())
    );
  });

  const handleJump = useCallback((link) => {
    if (!link) return;
    if (link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      navigate(link);
    }
  }, [navigate]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Notification Centre</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'} • {total} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" className="border-border gap-2" onClick={markAllRead}>
              <CheckCheck className="w-4 h-4" /> Mark all read
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => fetchNotifications()} title="Refresh">
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowPrefs(p => !p)} title="Preferences">
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Preferences */}
      {showPrefs && (
        <div className="p-4 rounded-xl border border-border bg-card">
          <PrefsPanel preferences={preferences} onSave={savePreferences} />
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search notifications..."
          className="pl-9"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-muted flex-wrap h-auto gap-1">
          <TabsTrigger value="all">All ({total})</TabsTrigger>
          <TabsTrigger value="unread">Unread ({unreadCount})</TabsTrigger>
          <TabsTrigger value="mentor">Mentor</TabsTrigger>
          <TabsTrigger value="application">Applications</TabsTrigger>
          <TabsTrigger value="match">AI Matches</TabsTrigger>
          <TabsTrigger value="milestone">Milestones</TabsTrigger>
          <TabsTrigger value="payment">Payments</TabsTrigger>
          <TabsTrigger value="certificate">Certificates</TabsTrigger>
          <TabsTrigger value="gamification">Gamification</TabsTrigger>
          <TabsTrigger value="change_request">Changes</TabsTrigger>
          <TabsTrigger value="event">Events</TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
              <p className="text-muted-foreground">No notifications found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(n => (
                <NotificationItem
                  key={n._id || n.id}
                  notif={n}
                  onRead={markRead}
                  onJump={handleJump}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
